import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { sanitizeHTML } from 'src/utils/sanitizeHTML'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { PRODUCT_INFORMATION_TAB_NAMES } from 'src/utils/amplitude/productInformationTabNames'

import MiniPdpBenefitsCarousel from './MiniPdpBenefitsCarousel'
import { MiniPdpProductDescription } from './AccordionSections/ProductDescription'
import { MiniPdpTechnicalContent } from './AccordionSections/TechnicalContent'
import {
  MiniPdpReviewsContent,
  MiniPdpReviewsTitle,
} from './AccordionSections/Reviews'
import type { Product } from '../../../types/product'
import type { ProductItem } from '../../../types/productItem'
import { useProductReviewSummary } from '../../../hooks/useProductReviewSummary'
import { hasValidContent } from './utils'
import { dispatchQuickViewEvent } from '../quickViewEvents'

import './MiniPdpInfoAccordion.scss'

type MiniPdpInfoAccordionProps = {
  product: Product
  currentSku: ProductItem
  onSectionOpen?: (sectionName: string) => void
}

type MiniPdpInfoSection = {
  id: string
  title: React.ReactNode
  eventName: string
  html?: string
  content?: React.ReactNode
}

export function MiniPdpInfoAccordion({
  product,
  currentSku,
  onSectionOpen,
}: MiniPdpInfoAccordionProps) {
  const { pageData } = useGlobalContext()
  const standardObject = getStandardObject(pageData)
  const productObject = getProductQueryObject(currentSku)
  const [openedSectionId, setOpenedSectionId] = useState<string | null>(null)
  const reviewsContentRef = useRef<HTMLDivElement>(null)
  const { review: fetchedReview, load: loadReviewSummary } =
    useProductReviewSummary(product.productId)

  const productBenefits = product.productBenefits ?? ''
  const reviewData = product.review ?? fetchedReview
  const reviewAverageNote = reviewData?.totalRatingsAverageNote

  const hasProductBenefits = hasValidContent(productBenefits)

  useLayoutEffect(() => {
    if (openedSectionId !== 'reviews') {
      return
    }

    const content = reviewsContentRef.current
    const scrollParent = content?.closest<HTMLElement>('[data-mini-pdp-scroll]')

    if (!content || !scrollParent) {
      return
    }

    const top =
      content.getBoundingClientRect().top -
      scrollParent.getBoundingClientRect().top +
      scrollParent.scrollTop

    scrollParent.scrollTo({ top: Math.max(0, top), behavior: 'auto' })
  }, [openedSectionId])

  useEffect(() => {
    const hasProductReview =
      typeof product.review?.totalRatingsAverageNote === 'number'

    if (hasProductReview) {
      return
    }

    loadReviewSummary()
  }, [product.review?.totalRatingsAverageNote, loadReviewSummary])

  const sections = useMemo<MiniPdpInfoSection[]>(() => {
    const sectionItems: MiniPdpInfoSection[] = []

    if (hasValidContent(product.description)) {
      sectionItems.push({
        id: 'description',
        title: 'Sobre o produto',
        eventName: PRODUCT_INFORMATION_TAB_NAMES.about,
        content: <MiniPdpProductDescription content={product.description} />,
      })
    }

    if (
      hasValidContent(product.technicalInformation) ||
      hasValidContent(product.productDocuments)
    ) {
      sectionItems.push({
        id: 'technical',
        title: 'Informações técnicas',
        eventName: PRODUCT_INFORMATION_TAB_NAMES.technical,
        content: (
          <MiniPdpTechnicalContent
            productDocuments={product.productDocuments}
            technicalInformation={product.technicalInformation}
          />
        ),
      })
    }

    if (reviewAverageNote) {
      sectionItems.push({
        id: 'reviews',
        title: <MiniPdpReviewsTitle reviewAverageNote={reviewAverageNote} />,
        eventName: PRODUCT_INFORMATION_TAB_NAMES.reviews,
        content: (
          <MiniPdpReviewsContent
            currentSku={currentSku}
            product={product}
            rating={fetchedReview}
          />
        ),
      })
    }

    return sectionItems
  }, [currentSku, fetchedReview, product, reviewAverageNote])

  if (!hasProductBenefits && sections.length === 0) {
    return null
  }

  return (
    <section className="mt-6">
      {hasProductBenefits && (
        <MiniPdpBenefitsCarousel content={productBenefits} />
      )}

      {sections.length > 0 && (
        <div className="divide-y divide-restructure-border-disabled border-y border-restructure-border-disabled">
          {sections.map((section) => {
            const isOpen = openedSectionId === section.id

            return (
              <article key={section.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between py-4 text-left"
                  onClick={() => {
                    if (!isOpen) {
                      if (onSectionOpen) {
                        onSectionOpen(section.eventName)
                      } else {
                        dispatchQuickViewEvent('Quick View Clicked', {
                          standardObject,
                          productObject,
                          extraData: {
                            'clicked section': section.eventName,
                          },
                        })
                      }
                    }

                    setOpenedSectionId(isOpen ? null : section.id)
                  }}
                  aria-expanded={isOpen}
                >
                  <span className="text-font-size-7 font-medium text-postal-primary">
                    {section.title}
                  </span>
                  <span className="text-xl leading-none text-fullDark">
                    {isOpen ? '-' : '+'}
                  </span>
                </button>

                {isOpen &&
                  (section.content ? (
                    <div
                      ref={
                        section.id === 'reviews' ? reviewsContentRef : undefined
                      }
                      data-testid={
                        section.id === 'reviews'
                          ? 'mini-pdp-reviews-content'
                          : undefined
                      }
                      className="pb-4"
                    >
                      {section.content}
                    </div>
                  ) : (
                    <div
                      className="mini-pdp-info-accordion__html pb-4 text-font-size-1 leading-6 text-secondary"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHTML(section.html ?? ''),
                      }}
                    />
                  ))}
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}
